"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUserRole = exports.hasPermission = exports.getPermissionsForRole = exports.getUserRole = exports.UserRole = void 0;
const react_1 = require("react");
const __1 = require("../");
var UserRole;
(function (UserRole) {
    UserRole["ADMIN"] = "Admin";
    UserRole["INSTALLER"] = "Installer";
    UserRole["USER"] = "User";
    UserRole["UNAUTHENTICATED"] = "Unauthenticated";
})(UserRole = exports.UserRole || (exports.UserRole = {}));
const unauthenticatedPermissions = {
    hub: { read: false, create: false, update: false, delete: false },
    sensor: { read: false, create: false, update: false, delete: false },
    sensorEvent: { read: false, create: false, update: false, delete: false },
    settings: { read: false, create: false, update: false, delete: false },
    users: { read: false, create: false, update: false, delete: false },
};
const userPermissions = {
    hub: { read: true, create: false, update: false, delete: false },
    sensor: { read: true, create: false, update: false, delete: false },
    sensorEvent: { read: true, create: false, update: false, delete: false },
    settings: { read: true, create: false, update: false, delete: false },
    users: { read: false, create: false, update: false, delete: false },
};
const installerPermissions = {
    hub: { read: true, create: true, update: true, delete: false },
    sensor: { read: true, create: true, update: true, delete: true },
    sensorEvent: { read: true, create: true, update: false, delete: false },
    settings: { read: true, create: false, update: false, delete: false },
    users: { read: false, create: false, update: false, delete: false },
};
const adminPermissions = {
    hub: { read: true, create: true, update: true, delete: true },
    sensor: { read: true, create: true, update: true, delete: true },
    sensorEvent: { read: true, create: true, update: true, delete: true },
    settings: { read: true, create: true, update: true, delete: true },
    users: { read: true, create: true, update: true, delete: true },
};
/**
 * Get the user's role from Cognito groups
 * @returns The user's role
 */
const getUserRole = async () => {
    try {
        const currentUser = await __1.Auth.currentAuthenticatedUser();
        if (!currentUser) {
            return UserRole.UNAUTHENTICATED;
        }
        const session = await __1.Auth.currentSession();
        if (!session) {
            return UserRole.UNAUTHENTICATED;
        }
        const idTokenClaims = session.tokens?.idToken?.payload;
        if (!idTokenClaims) {
            return UserRole.UNAUTHENTICATED;
        }
        const groups = idTokenClaims['cognito:groups'] || [];
        if (groups.some((group) => group.includes('AdminGroup'))) {
            return UserRole.ADMIN;
        }
        else if (groups.some((group) => group.includes('InstallerGroup'))) {
            return UserRole.INSTALLER;
        }
        else if (groups.some((group) => group.includes('UserGroup'))) {
            return UserRole.USER;
        }
        return UserRole.USER;
    }
    catch (error) {
        console.error('Error getting user role:', error);
        return UserRole.UNAUTHENTICATED;
    }
};
exports.getUserRole = getUserRole;
/**
 * Get permissions for a specific user role
 * @param role The user role
 * @returns The permissions for the specified role
 */
const getPermissionsForRole = (role) => {
    switch (role) {
        case UserRole.ADMIN:
            return adminPermissions;
        case UserRole.INSTALLER:
            return installerPermissions;
        case UserRole.USER:
            return userPermissions;
        case UserRole.UNAUTHENTICATED:
        default:
            return unauthenticatedPermissions;
    }
};
exports.getPermissionsForRole = getPermissionsForRole;
/**
 * Check if the current user has permission to perform an action on a resource
 * @param resource The resource to check permissions for
 * @param action The action to check (read, create, update, delete)
 * @returns Promise that resolves to a boolean indicating if the user has permission
 */
const hasPermission = async (resource, action) => {
    const userRole = await (0, exports.getUserRole)();
    const permissions = (0, exports.getPermissionsForRole)(userRole);
    return permissions[resource][action];
};
exports.hasPermission = hasPermission;
/**
 * React hook to get the current user's role
 * @returns Object containing the user role and loading state
 */
const useUserRole = () => {
    const [role, setRole] = (0, react_1.useState)(null);
    const [permissions, setPermissions] = (0, react_1.useState)(unauthenticatedPermissions);
    const [isInitialized, setIsInitialized] = (0, react_1.useState)(false);
    const [isFetchingUserRole, setIsFetchingUserRole] = (0, react_1.useState)(false);
    const fetchUserRole = async () => {
        setIsFetchingUserRole(true);
        try {
            const userRole = await (0, exports.getUserRole)();
            setRole(userRole);
            setPermissions((0, exports.getPermissionsForRole)(userRole));
            return userRole;
        }
        catch (error) {
            console.error('Error fetching user role:', error);
        }
        finally {
            setIsFetchingUserRole(false);
        }
    };
    (0, react_1.useEffect)(() => {
        if (role !== null) {
            setIsInitialized(true);
        }
    }, [role]);
    (0, react_1.useEffect)(() => {
        fetchUserRole();
    }, []);
    return { role, permissions, isFetchingUserRole, isInitialized, fetchUserRole };
};
exports.useUserRole = useUserRole;
